import "./PagesSettingsView.styl";
import { createJsonbObjectEditor } from "../_resources/jsonbObjectEditor.js";
import { deletePageFromDB, copyPageData, getMatchingLocales, getMatchingToolIds } from "../_resources/supa-helpers.js";
import { holdButton } from "../_resources/ui-library.js";
import { supabaseClient } from "../auth/supabase.service.js";

export default function ({ dom, message, filePicker }) {

  const render = (pageId, base_url, slug, site, {good, bad, refresh}, hasPageEditAccess, hasPageOwnerAccess) => {

    const editorDom = dom.append("section").class("editor-section");
    const copySection = dom.append("section").class("copy-section");
    const dangerSection = dom.append("section").class("bad");

    const editor = createJsonbObjectEditor({
      dom: editorDom,
      message,
      heading: "Settings",
      table: 'pages',
      recordId: pageId,
      fieldName: 'theme_meta', 
      base_url,
      schema: {
        "title": {type: "text", label: "Page title", comment: "appears in browser tab and used by search engines"},
        "type": {type: "text", label: "Page type for social media", value: "website", comment: "set to 'website'"},
        "description": {type: "text", label: "Description for social media"},
        "favicon": {type: "image", label: "Favicon image", comment: `uploaded file, global https url or asset/path`},
        "socialImage": {type: "image", label: "Social image", comment: `uploaded file, global https url or asset/path`},
      },
      allowEdit: hasPageEditAccess,
      filePicker
    });


    // Copy section
    copySection.append("h1").text("Copy content from another page");
    copySection.append("p").text("Here you can copy settings including translations and deep configs from an existing page into this one")
    copySection.append("p").text("This is useful when creating a new page similar to an existing one or if you messed up the configs and want to restore.");
    
    renderCopyForm(copySection, pageId, {good, bad, refresh});

    async function renderCopyForm(container, targetPageId, {good, bad, refresh}) {
      // Fetch all pages for the dropdown
      const { data: allPages, error: pagesError } = await supabaseClient
        .from('pages')
        .select('id, slug, site')
        .neq('id', targetPageId)
        .order('site', { ascending: true })
        .order('slug', { ascending: true });
      
      if (pagesError) {
        container.append("p").text("Error loading pages list");
        return;
      }

      const form = container.append("form").class("copy-form");
      
      // Source page selector
      const sourceRow = form.append("div").class("form-row");
      sourceRow.append("label").attr("for", "source-page").class("label required").text("Copy from page:");
      const sourceSelect = sourceRow.append("select").attr("id", "source-page").class("input");
      sourceSelect.append("option").attr("value", "").text("-- Select source page --");
      
      allPages.forEach(page => {
        const label = page.slug === "__home__" ? `${page.site} (home)` : `${page.site}/${page.slug}`;
        sourceSelect.append("option").attr("value", page.id).text(label);
      });
      
      // Page fields checkboxes
      const pageFieldsConfig = [
        { field: "datasources", label: "📒 Data Sources" },
        { field: "toolset", label: "🧰 Toolset" },
        { field: "locales", label: "文 Locale list" },
        { field: "menu_items", label: "🎹 Menu items" },
        { field: "related", label: "🍀 Related items" },
        { field: "footer_links", label: "⛸ Footer links" },
        { field: "footer_logos", label: "⛸ Footer logos" },
        { field: "theme_variables", label: "🎨 Theme" },
        { field: "theme_components", label: "🧩 Component settings" },
        { field: "theme_style", label: "🌶 Styles" },
        { field: "theme_layout", label: "🎛 Layout" },
        { field: "theme_fonts", label: "🖋 Fonts" },
        { field: "concept_mapping", label: "↩️ Concept mapping" },
        { field: "entityset_mapping", label: "↩️ Entity set mapping" },
        { field: "theme_meta", label: "⚙️ Settings (above)" },
      ];
      
      const fieldsRow = form.append("div").class("form-row");
      fieldsRow.append("label").class("label").text("Page fields to copy:");
      const fieldsGrid = fieldsRow.append("div").class("checkbox-grid");
      
      pageFieldsConfig.forEach(({ field, label }) => {
        const fieldItem = fieldsGrid.append("label").class("checkbox-label");
        fieldItem.append("input")
          .attr("type", "checkbox")
          .attr("class", "checkbox page-field-checkbox")
          .attr("data-field", field);
        fieldItem.append("span").text(label);
      });
      
      // Translations section with filters
      const translationsRow = form.append("div").class("form-row translations-section");
      const translationsHeader = translationsRow.append("div").class("section-header");
      translationsHeader.append("input")
        .attr("type", "checkbox")
        .attr("id", "copy-translations")
        .attr("class", "checkbox")
        .property("checked", false)
        .on("change", function() {
          const isChecked = d3.select(this).property("checked");
          translationsFilters.style("display", isChecked ? "block" : "none");
        });
      translationsHeader.append("label")
        .attr("for", "copy-translations")
        .class("section-label")
        .text("文 Translations");
      
      const translationsFilters = translationsRow.append("div")
        .class("filter-group")
        .style("display", "none");
      
      // Translation scope filters
      const scopeFilter = translationsFilters.append("div").class("filter-item");
      scopeFilter.append("label").class("filter-label").text("Scope:");
      const scopeGrid = scopeFilter.append("div").class("checkbox-grid");
      
      ["vizabi", "page"].forEach(scope => {
        const scopeItem = scopeGrid.append("label").class("checkbox-label");
        scopeItem.append("input")
          .attr("type", "checkbox")
          .attr("class", "checkbox translation-scope-checkbox")
          .attr("data-scope", scope)
          .property("checked", true);
        scopeItem.append("span").text(scope);
      });
      
      // Translation locale filters (will be populated when source is selected)
      const localeFilter = translationsFilters.append("div").class("filter-item");
      localeFilter.append("label").class("filter-label").text("Locales:");
      const localeGrid = localeFilter.append("div").class("checkbox-grid").attr("id", "locale-grid");
      localeGrid.append("p").class("filter-hint").text("Select a source page to see matching locales");
      
      // Configs section with filters
      const configsRow = form.append("div").class("form-row configs-section");
      const configsHeader = configsRow.append("div").class("section-header");
      configsHeader.append("input")
        .attr("type", "checkbox")
        .attr("id", "copy-configs")
        .attr("class", "checkbox")
        .property("checked", false)
        .on("change", function() {
          const isChecked = d3.select(this).property("checked");
          configsFilters.style("display", isChecked ? "block" : "none");
        });
      configsHeader.append("label")
        .attr("for", "copy-configs")
        .class("section-label")
        .text("💹 Deep configs");
      
      const configsFilters = configsRow.append("div")
        .class("filter-group")
        .style("display", "none");
      
      // Config type filters
      const typeFilter = configsFilters.append("div").class("filter-item");
      typeFilter.append("label").class("filter-label").text("Type:");
      const typeGrid = typeFilter.append("div").class("checkbox-grid");
      
      ["essential", "preferential"].forEach(type => {
        const typeItem = typeGrid.append("label").class("checkbox-label");
        typeItem.append("input")
          .attr("type", "checkbox")
          .attr("class", "checkbox config-type-checkbox")
          .attr("data-type", type)
          .property("checked", true);
        typeItem.append("span").text(type);
      });
      
      // Config tool filters (will be populated when source is selected)
      const toolFilter = configsFilters.append("div").class("filter-item");
      toolFilter.append("label").class("filter-label").text("Tools:");
      const toolGrid = toolFilter.append("div").class("checkbox-grid").attr("id", "tool-grid");
      toolGrid.append("p").class("filter-hint").text("Select a source page to see matching tools");
      
      // Handle source page selection to populate dynamic filters
      sourceSelect.on("change", async function() {
        const sourcePageId = d3.select(this).property("value");
        if (!sourcePageId) {
          localeGrid.html("").append("p").class("filter-hint").text("Select a source page to see matching locales");
          toolGrid.html("").append("p").class("filter-hint").text("Select a source page to see matching tools");
          return;
        }
        
        // Fetch and populate matching locales
        const { data: matchingLocales, error: localesError } = await getMatchingLocales(sourcePageId, targetPageId);
        if (!localesError && matchingLocales && matchingLocales.length > 0) {
          localeGrid.html("");
          matchingLocales.forEach(locale => {
            const localeItem = localeGrid.append("label").class("checkbox-label");
            localeItem.append("input")
              .attr("type", "checkbox")
              .attr("class", "checkbox translation-locale-checkbox")
              .attr("data-locale", locale)
              .property("checked", true);
            localeItem.append("span").text(locale);
          });
        } else {
          localeGrid.html("").append("p").class("filter-hint").text("No matching locales found");
        }
        
        // Fetch and populate matching tools
        const { data: matchingTools, error: toolsError } = await getMatchingToolIds(sourcePageId, targetPageId);
        if (!toolsError && matchingTools && matchingTools.length > 0) {
          toolGrid.html("");
          matchingTools.forEach(toolId => {
            const toolItem = toolGrid.append("label").class("checkbox-label");
            toolItem.append("input")
              .attr("type", "checkbox")
              .attr("class", "checkbox config-tool-checkbox")
              .attr("data-tool", toolId)
              .property("checked", true);
            toolItem.append("span").text(toolId);
          });
        } else {
          toolGrid.html("").append("p").class("filter-hint").text("No matching tools found");
        }
      });
      
      // Replace existing checkbox
      const replaceRow = form.append("div").class("form-row");
      const replaceLabel = replaceRow.append("label").class("checkbox-label");
      replaceLabel.append("input")
        .attr("type", "checkbox")
        .attr("id", "replace-existing")
        .attr("class", "checkbox")
        .property("checked", false);
      replaceLabel.append("span").text("🔥 Replace existing data (delete before copying)");
      
      // Prevent form submission
      form.on("submit", (event) => {
        event.preventDefault();
      });
      
      // Submit button
      const buttonRow = form.append("div").class("form-row");
      const copyButton = buttonRow.append("button")
        .attr("type", "button")
        .attr("class", "button bigger hold-btn")
        .classed("disabled", !hasPageEditAccess)
        .text("Copy selected data");
      
      holdButton(copyButton, async () => {
        const sourcePageId = sourceSelect.property("value");
        if (!sourcePageId) {
          return bad("Please select a source page");
        }
        
        // Collect selected page fields
        const pageFields = [];
        form.selectAll(".page-field-checkbox").each(function() {
          const checkbox = d3.select(this);
          if (checkbox.property("checked")) {
            pageFields.push(checkbox.attr("data-field"));
          }
        });
        
        const copyTranslations = form.select("#copy-translations").property("checked");
        const copyConfigs = form.select("#copy-configs").property("checked");
        const replaceExisting = form.select("#replace-existing").property("checked");
        
        // Collect translation filters
        const translationScopes = [];
        form.selectAll(".translation-scope-checkbox").each(function() {
          const checkbox = d3.select(this);
          if (checkbox.property("checked")) {
            translationScopes.push(checkbox.attr("data-scope"));
          }
        });
        
        const translationLocales = [];
        form.selectAll(".translation-locale-checkbox").each(function() {
          const checkbox = d3.select(this);
          if (checkbox.property("checked")) {
            translationLocales.push(checkbox.attr("data-locale"));
          }
        });
        
        // Collect config filters
        const configTypes = [];
        form.selectAll(".config-type-checkbox").each(function() {
          const checkbox = d3.select(this);
          if (checkbox.property("checked")) {
            configTypes.push(checkbox.attr("data-type"));
          }
        });
        
        const configToolIds = [];
        form.selectAll(".config-tool-checkbox").each(function() {
          const checkbox = d3.select(this);
          if (checkbox.property("checked")) {
            configToolIds.push(checkbox.attr("data-tool"));
          }
        });
        
        if (pageFields.length === 0 && !copyTranslations && !copyConfigs) {
          return bad("Please select at least one item to copy");
        }
        
        // Call the copy helper with filters
        const { data, error } = await copyPageData(sourcePageId, targetPageId, {
          pageFields,
          copyTranslations,
          copyConfigs,
          replaceExisting,
          translationFilters: {
            scopes: translationScopes,
            locales: translationLocales
          },
          configFilters: {
            types: configTypes,
            toolIds: configToolIds
          }
        });
        
        if (error) {
          return bad("Error copying data: " + JSON.stringify(error));
        }
        
        let message = "Successfully copied: ";
        const parts = [];
        if (data.pageFieldsCopied.length > 0) {
          parts.push(`${data.pageFieldsCopied.length} page field(s)`);
        }
        if (data.translationsCopied > 0) {
          parts.push(`${data.translationsCopied} translation(s)`);
        }
        if (data.configsCopied > 0) {
          parts.push(`${data.configsCopied} config(s)`);
        }
        message += parts.join(", ");
        
        good(message, 3000);
        refresh();
      });
    }




    // Danger zone section
    dangerSection.append("h1").text("Danger zone");
    dangerSection.append("p").text("Here you can transfer the page to a different site, change the slug or delete the page entirely.");


    // Create inputs for site, slug, base_url
    const fields = [
      { key: 'site', label: 'Site', value: site, comment: "This setting should be updated together with the page local config. See the file properties.json" },
      { key: 'slug', label: 'Slug', value: slug, comment: "The old /slug will not be reachable! Setting slug to '__home__' makes the page the home page of the site if there isn't one yet"},
      { key: 'base_url', label: 'Base URL', value: base_url, comment: "The page should be deployed at the target URL" }
    ];

    dangerSection.append("table").class("admin-table")
      .selectAll("tr")
      .data(fields)
      .enter().append("tr")
      .each(function({ key, label, value, comment}) {
        const fieldRow = d3.select(this);
        
        fieldRow.append("td").append("label").text(label + ':');
        const inputCell = fieldRow.append("td");
        const input = inputCell.append("input")
          .class("input")
          .attr('type', 'text')
          .attr('id', `page-${key}`)
          .property('value', value || '');
        
        // Add comment if provided
        if (comment) {
          inputCell.append("small")
            .style('color', '#999')
            .style('font-size', '0.85em')
            .style('margin-top', '4px')
            .style('display', 'block')
            .html(comment);
        }
        
        const applyButton = fieldRow.append("td").append("button")
          .attr('class', 'button bigger hold-btn')
          .classed("disabled", !hasPageOwnerAccess)
          .text('Apply');
        holdButton(applyButton, () => updatePageField(key, input.node().value));
    });


    
    if (slug === "__home__"){
      dangerSection.append("div").text("Deleting home page is a big destructive operation and is not yet supported");
    } else {
      dangerSection.append("p").html(`Comletely delete the page and all associated configs <br/> DO OR NOT DO, THERE IS NO UNDO YET!`);

      const deleteButton = dangerSection.append("button")
        .class("button delete bigger hold-btn")
        .classed("disabled", !hasPageOwnerAccess)
        .text("✘ Delete Page");
      holdButton(deleteButton, () => deletePage(pageId));
    }

    async function deletePage(pageId) {
      const {error} = await deletePageFromDB(pageId)
      if (error) return bad('Error deleting page: ' + JSON.stringify(error)); //❌
      good("Page deleted successfully", 2000); //✅
      refresh();
    }

    async function updatePageField(field, value) {
      const { error } = await supabaseClient
        .from('pages')
        .update({ [field]: value })
        .eq('id', pageId);
      
      if (error) return bad('Error updating field ' + field + ': ' + JSON.stringify(error)); //❌
      good("✓ Applied", 2000); //✅
      refresh();
    }


    editor.load();
  };

  return { render };
}
